import { useEffect, useState } from "react";
import { toast } from "sonner";

import radioCheck from "assets/svg/radio-check.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionAttendHearing,
  saveOppositionAttendHearing,
  type OppositionAttendHearingItem,
  type OppositionAttendHearingStatus,
  type OppositionHearingOfficerRemark,
} from "api/opposition";

export const OPPOSITION_ATTEND_HEARING_FORM_ID =
  "opposition-attend-hearing-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const TEXTAREA_CLASS =
  "min-h-[80px] w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] resize-y";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Draft = {
  hearingStatus: OppositionAttendHearingStatus | "";
  remarkOfHearingOfficer: OppositionHearingOfficerRemark | "";
  directionOfOfficer: string;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionAttendHearingView({ leadId, onSuccess }: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<OppositionAttendHearingItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionAttendHearing(leadId)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        const next: Record<string, Draft> = {};
        data.items.forEach((it) => {
          next[it.conflictingMarkId] = {
            hearingStatus: it.hearingStatus ?? "",
            remarkOfHearingOfficer: it.remarkOfHearingOfficer ?? "",
            directionOfOfficer: it.directionOfOfficer ?? "",
          };
        });
        setDrafts(next);
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Attend Hearing"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeItem = items[activeIdx] ?? null;
  const activeDraft = activeItem ? drafts[activeItem.conflictingMarkId] : null;

  const updateDraft = (patch: Partial<Draft>) => {
    if (!activeItem) return;
    setDrafts((prev) => ({
      ...prev,
      [activeItem.conflictingMarkId]: {
        ...prev[activeItem.conflictingMarkId],
        ...patch,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving || !activeItem || !activeDraft) return;
    if (!activeDraft.hearingStatus) {
      toast.error("Select Hearing Status.");
      return;
    }
    if (
      activeDraft.hearingStatus === "ATTENDED" &&
      !activeDraft.remarkOfHearingOfficer
    ) {
      toast.error("Select Remark of Hearing Officer when status is Attended.");
      return;
    }
    if (
      activeDraft.remarkOfHearingOfficer === "DIRECTION_OF_OFFICER" &&
      !activeDraft.directionOfOfficer.trim()
    ) {
      toast.error("Enter the Direction of Officer text.");
      return;
    }
    setSaving(true);
    try {
      const fresh = await saveOppositionAttendHearing(
        leadId,
        activeItem.conflictingMarkId,
        {
          hearingStatus: activeDraft.hearingStatus as OppositionAttendHearingStatus,
          remarkOfHearingOfficer:
            activeDraft.hearingStatus === "ATTENDED"
              ? (activeDraft.remarkOfHearingOfficer as OppositionHearingOfficerRemark)
              : null,
          directionOfOfficer:
            activeDraft.remarkOfHearingOfficer === "DIRECTION_OF_OFFICER" ||
            (activeDraft.remarkOfHearingOfficer === "FILE_TM_M" &&
              activeDraft.directionOfOfficer.trim())
              ? activeDraft.directionOfOfficer.trim() || null
              : null,
        }
      );
      const updatedItems = items.map((it) =>
        it.conflictingMarkId === activeItem.conflictingMarkId ? fresh : it,
      );
      setItems(updatedItems);
      toast.success("Hearing attendance saved.");

      // Per-TM routing: each conflicting mark is decided independently. If any
      // other mark on this lead still has no saved hearingStatus, switch to that
      // tab instead of advancing the stepper — otherwise the lead-level routing
      // flags (anyRequiresInformAfterHearing, etc.) would be true purely because
      // an unsaved mark has null status, and the user would be routed to Inform
      // Client after Hearing even when the just-saved mark was DIDNT_HAPPEN.
      const nextUnsavedIdx = updatedItems.findIndex(
        (it) => it.hearingStatus == null,
      );
      if (nextUnsavedIdx >= 0) {
        setActiveIdx(nextUnsavedIdx);
        return;
      }

      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save hearing attendance"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (items.length === 0 || !activeItem || !activeDraft) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No applications with a Hearing date set. Save Step 26 first.
        </p>
      </div>
    );
  }

  const showRemark = activeDraft.hearingStatus === "ATTENDED";
  const showDirection =
    activeDraft.remarkOfHearingOfficer === "DIRECTION_OF_OFFICER" ||
    activeDraft.remarkOfHearingOfficer === "FILE_TM_M";

  return (
    <form
      id={OPPOSITION_ATTEND_HEARING_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {items.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {items.map((it, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={it.conflictingMarkId}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(it.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Mark</label>
          <input type="text" value={activeItem.mark ?? ""} readOnly className={READONLY_CLASS} />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Opposition Hearing</label>
          <input
            type="text"
            value={activeItem.dateOfOppositionHearing ?? ""}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Hearing Status</label>
          <Select
            value={activeDraft.hearingStatus || ""}
            onValueChange={(v) =>
              updateDraft({
                hearingStatus: v as OppositionAttendHearingStatus,
                remarkOfHearingOfficer: v === "ATTENDED" ? activeDraft.remarkOfHearingOfficer : "",
                directionOfOfficer: v === "ATTENDED" ? activeDraft.directionOfOfficer : "",
              })
            }
          >
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ATTENDED">Attended</SelectItem>
              <SelectItem value="NOT_ATTENDED">Not Attended</SelectItem>
              <SelectItem value="DIDNT_HAPPEN">Didn&apos;t Happen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {showRemark && (
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Remark of Hearing Officer</label>
            <Select
              value={activeDraft.remarkOfHearingOfficer || ""}
              onValueChange={(v) =>
                updateDraft({
                  remarkOfHearingOfficer: v as OppositionHearingOfficerRemark,
                  directionOfOfficer:
                    v === "DIRECTION_OF_OFFICER" || v === "FILE_TM_M"
                      ? activeDraft.directionOfOfficer
                      : "",
                })
              }
            >
              <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px]">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACCEPTED">Accepted</SelectItem>
                <SelectItem value="REFUSED">Refused</SelectItem>
                <SelectItem value="WILL_PASS_ON_MERITS">Will pass on merits</SelectItem>
                <SelectItem value="FILE_TM_M">File TM-M</SelectItem>
                <SelectItem value="DIRECTION_OF_OFFICER">Direction of Officer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {showDirection && (
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Direction of Officer</label>
          <textarea
            value={activeDraft.directionOfOfficer}
            onChange={(e) => updateDraft({ directionOfOfficer: e.target.value })}
            rows={3}
            placeholder="Enter the officer's direction…"
            className={TEXTAREA_CLASS}
            disabled={saving}
          />
        </div>
      )}
    </form>
  );
}
